import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { PhoneNumberForm } from "@/components/auth/PhoneNumberForm";
import { VerificationForm } from "@/components/auth/VerificationForm";
import { usePhoneAuth } from "@/hooks/usePhoneAuth";
import { useAuth } from "@/context/auth-provider";
import { useNavigate } from "react-router-dom";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useOrganizationBranding } from "@/hooks/useOrganizationBranding";
const Auth = () => {
  const navigate = useNavigate();
  const {
    user,
    loading: authLoading
  } = useAuth();
  const {
    organizationName,
    organizationLogo,
    logoAltText
  } = useOrganizationBranding();
  const {
    phoneNumber,
    setPhoneNumber,
    verificationCode,
    setVerificationCode,
    isVerifying,
    loading: phoneAuthLoading,
    handleSendOTP,
    handleVerifyOTP
  } = usePhoneAuth();

  // Redirect authenticated users to app page
  useEffect(() => {
    if (user && !authLoading) {
      navigate('/app', {
        replace: true
      });
    }
  }, [user, authLoading, navigate]);

  // Show loading state while checking authentication
  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-foreground">Checking authentication...</p>
      </div>;
  }

  // If user is already authenticated, don't render the auth form
  if (user) {
    return <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-foreground">Redirecting...</p>
      </div>;
  }
  return <div className="min-h-screen flex flex-col items-center justify-center px-4 py-6 sm:px-6 lg:px-8 bg-gradient-to-br from-background to-muted/20">
      <Card className="w-full max-w-md p-8 sm:p-10 bg-card/95 backdrop-blur-sm shadow-2xl border border-border/50 rounded-2xl">
        {/* Brand area with organization name and logo */}
        <div className="mb-8 flex flex-col items-center text-center">
          {/* Organization name - prominent and modern */}
          <h1 className="text-2xl sm:text-3xl font-bold text-card-foreground mb-6 tracking-tight">
            {organizationName}
          </h1>
          
          {/* Logo container with enhanced styling */}
          <div className="w-40 h-40 sm:w-48 sm:h-48 mx-auto mb-6 flex items-center justify-center p-3 rounded-none bg-transparent">
            <img src={organizationLogo} alt={logoAltText} className="w-full h-full object-contain" style={{
            maxWidth: '100%',
            maxHeight: '100%'
          }} onError={e => {
            const target = e.target as HTMLImageElement;
            if (target.src !== "/uploads/d4215903-dca2-4d49-9f0f-47cc0cd1fe09.png") {
              target.src = "/uploads/d4215903-dca2-4d49-9f0f-47cc0cd1fe09.png";
            }
          }} />
          </div>
        </div>

        {/* Welcome section with improved typography */}
        <div className="text-center mb-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-card-foreground mb-3">
            Welcome, let's sign in!
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
            Enter your phone number to receive a verification code
          </p>
        </div>

        {/* Form section with better spacing */}
        <div className="space-y-6">
          {!isVerifying ? <PhoneNumberForm phoneNumber={phoneNumber} loading={phoneAuthLoading} onPhoneNumberChange={setPhoneNumber} onSubmit={handleSendOTP} /> : <VerificationForm verificationCode={verificationCode} loading={phoneAuthLoading} onVerificationCodeChange={setVerificationCode} onSubmit={handleVerifyOTP} />}
        </div>
        
        {/* Enhanced footer with better styling */}
        <div className="mt-10 pt-6 border-t border-border/50 text-center space-y-2">
          <p className="text-sm font-medium text-muted-foreground">Parcel Pilot</p>
          <p className="text-xs text-muted-foreground/80">Created by Happyhood, Powered by Send Swift</p>
        </div>
      </Card>
    </div>;
};
export default Auth;